import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Dialog, Button, IconButton } from '@material-ui/core'
import { Close } from '@material-ui/icons'
import { Spinner } from 'ui'

// --------------- Styled components ---------------

const DupDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 16px;
    overflow: hidden;
    max-width: 460px;
    width: 100%;
  }
`
const DupHeaderSection = styled.div`
  position: relative;
  background: #FCD34D;
  padding: 24px 24px 28px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`
const DupCloseBtn = styled(IconButton)`
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 4px;
`
const DupIconWrap = styled.div`
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.45);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
`
const DupTitle = styled.div`
  font-family: Rubik;
  font-size: 20px;
  font-weight: 700;
  color: #0F172A;
`
const DupDescription = styled.div`
  font-family: Rubik;
  font-size: 14px;
  color: #1E293B;
  line-height: 1.7;
  max-width: 360px;
`
const DupBodySection = styled.div`
  background: #FFF;
  padding: 16px 20px 0;
`
const DupNameRow = styled.div`
  display: flex;
  /* @noflip */ direction: rtl;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  font-family: Rubik;
  font-size: 16px;
  font-weight: 500;
  color: #0F172A;
  border-bottom: 1px solid #F1F5F9;
`
const DupInfoRow = styled.div<{ highlight?: boolean }>`
  display: flex;
  /* @noflip */ direction: rtl;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  margin: 4px 0;
  font-family: Rubik;
  font-size: 14px;
  color: #1E293B;
  background: ${p => p.highlight ? '#FEF9C3' : 'transparent'};
  border: 1px solid ${p => p.highlight ? '#FDE68A' : 'transparent'};
`
const DupInfoText = styled.span`
  flex: 1;
  /* @noflip */ direction: ltr;
  text-align: right;
`
const DupArrow = styled.span`
  font-size: 16px;
  color: #92400E;
  font-weight: 600;
  margin-right: auto;
`
const DupIconCircle = styled.div<{ bg: string }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${p => p.bg};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 16px;
`

const DupActions = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 20px 20px;
  background: #FFF;
`
const DupBackButton = styled(Button)`
  flex: 1;
  padding: 10px !important;
  border-radius: 8px !important;
  background: #DC2626 !important;
  color: #FFF !important;
  font-family: Rubik !important;
  font-size: 14px !important;
  font-weight: 500 !important;
`
const DupUseButton = styled(Button)`
  flex: 1;
  padding: 10px !important;
  border-radius: 8px !important;
  background: #2563EB !important;
  color: #FFF !important;
  font-family: Rubik !important;
  font-size: 14px !important;
  font-weight: 500 !important;
`

// --------------- Helpers ---------------

const digitsOnly = (s: string) => (s ?? '').replace(/\D/g, '').slice(-9)

const formatPhone = (p: string) =>
  p?.replace(/^\+972(\d{2})(\d{7})$/, '0$1-$2') ?? p

// --------------- Props ---------------

type Props = {
  open: boolean
  existingUser: {
    existingUserId: number
    existingGivenName?: string
    existingFamilyName?: string
    existingMainMobile?: string
    existingEmail?: string
    existingIsraeliId?: string
  }
  saving: boolean
  onBack: () => void
  onUseExisting: () => void
  formPhone?: string
  formEmail?: string
  formIsraeliId?: string
}

// --------------- Component ---------------

const DuplicateUserModal: FC<Props> = ({
  open,
  existingUser,
  saving,
  onBack,
  onUseExisting,
  formPhone,
  formEmail,
  formIsraeliId,
}) => {
  const { t } = useTranslation('population')
  const name = [existingUser?.existingGivenName, existingUser?.existingFamilyName]
    .filter(Boolean).join(' ')
  const phone = existingUser?.existingMainMobile
  const email = existingUser?.existingEmail
  const israeliId = existingUser?.existingIsraeliId

  const phoneConflict = !!phone && !!formPhone && digitsOnly(phone) === digitsOnly(formPhone)
  const emailConflict = !!email && !!formEmail && email.toLowerCase() === formEmail.toLowerCase()
  const idConflict = !!israeliId && !!formIsraeliId && israeliId === formIsraeliId

  return (
    <DupDialog open={open} style={{ zIndex: 1300 }} maxWidth="xs" fullWidth>
      <DupHeaderSection>
        <DupCloseBtn onClick={onBack}>
          <Close style={{ color: '#92400E', fontSize: 20 }} />
        </DupCloseBtn>
        <DupIconWrap>🪪</DupIconWrap>
        <DupTitle>{t`duplicate_title`}</DupTitle>
        <DupDescription>
          {t`duplicate_body`}
        </DupDescription>
      </DupHeaderSection>

      <DupBodySection>
        <DupNameRow>
          <DupIconCircle bg="#E2E8F0">👤</DupIconCircle>
          <DupInfoText>{name}</DupInfoText>
        </DupNameRow>

        {phone && (
          <DupInfoRow highlight={phoneConflict}>
            <DupIconCircle bg={phoneConflict ? '#FEF3C7' : '#F1F5F9'}>📞</DupIconCircle>
            <DupInfoText>{formatPhone(phone)}</DupInfoText>
            {phoneConflict && <DupArrow>→</DupArrow>}
          </DupInfoRow>
        )}

        {email && (
          <DupInfoRow highlight={emailConflict}>
            <DupIconCircle bg={emailConflict ? '#DBEAFE' : '#F1F5F9'}>✉</DupIconCircle>
            <DupInfoText>{email}</DupInfoText>
            {emailConflict && <DupArrow>→</DupArrow>}
          </DupInfoRow>
        )}

        {israeliId && (
          <DupInfoRow highlight={idConflict}>
            <DupIconCircle bg={idConflict ? '#FCE7F3' : '#F1F5F9'}>🪪</DupIconCircle>
            <DupInfoText>{israeliId}</DupInfoText>
            {idConflict && <DupArrow>→</DupArrow>}
          </DupInfoRow>
        )}
      </DupBodySection>

      <DupActions>
        <DupBackButton onClick={onBack}>{t`back_to_edit`}</DupBackButton>
        <DupUseButton onClick={onUseExisting} disabled={saving}>
          {saving ? <Spinner size={18} /> : t`use_existing_card`}
        </DupUseButton>
      </DupActions>
    </DupDialog>
  )
}

export default DuplicateUserModal
